import { useEffect } from 'react';
import { motion, MotionConfig } from 'framer-motion';
import '@/styles/canva-brand-kit.css';

/* External Canva Business link — replace this placeholder when the
   official Canva Brand Kit URL is provisioned. Every "Open Canva
   Brand Kit" CTA on this page reads from this single constant. */
const CANVA_BRAND_KIT_URL = 'PASTE_CANVA_BUSINESS_LINK_HERE';

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ delay, duration: 0.95, ease: EASE }}
  >
    {children}
  </motion.div>
);

const KIT_ITEMS = [
  'Logo files',
  'Monogram mark',
  'Brand colour palette',
  'Typography system',
  'Social media templates',
  'Instagram story templates',
  'Campaign graphics',
  'Presentation templates',
  'Press and media assets',
  'Partner materials',
];

const MOOD = ['Cinematic', 'Private', 'Refined', 'Feminine', 'Luxe', 'Intimate', 'Discreet', 'Magnetic'];

const RULES = [
  'Use only approved NYMPHÉ assets from Canva Business.',
  'Do not alter, distort, recolour, or recreate the logo.',
  'Do not use off-brand colours, fonts, or imagery.',
  'Do not use explicit, cheap, spammy, or overly aggressive visual language.',
  'Keep all creative work premium, discreet, and polished.',
];

const CanvaBrandKitPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <main className="cbk-page" data-testid="canva-brand-kit-page">
        {/* ─── Hero / Portal entry ───────────────────────────────── */}
        <section className="cbk-hero" data-testid="cbk-hero">
          <div className="cbk-hero__glow" aria-hidden="true" />
          <div className="cbk-container">
            <Reveal>
              <div className="cbk-lock" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                </svg>
                <span>Private brand portal · by invitation</span>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <h1 className="cbk-h1" data-testid="cbk-page-title">NYMPHÉ Canva Brand Kit</h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="cbk-hero__lede">
                Official brand assets, templates, colours, typography, and creative
                direction for approved NYMPHÉ collaborators.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <p className="cbk-hero__sub">
                This private brand portal connects approved collaborators to the
                official NYMPHÉ Canva Business Brand Kit. Use it to access approved
                visual assets, social templates, campaign graphics, presentation
                decks, brand colours, typography, and creative guidance.
              </p>
            </Reveal>

            <Reveal delay={0.32}>
              <div className="cbk-cta-row">
                <a
                  className="cbk-btn cbk-btn--primary"
                  href={CANVA_BRAND_KIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cbk-primary-cta"
                >
                  Open Canva Brand Kit
                </a>
                <a
                  className="cbk-btn cbk-btn--ghost"
                  href="mailto:hello@nymphe.co?subject=NYMPHÉ%20Canva%20Brand%20Kit%20Access"
                  data-testid="cbk-request-cta"
                >
                  Request Brand Access
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.40}>
              <p className="cbk-access-note" data-testid="cbk-access-note">
                Access is reserved for approved NYMPHÉ collaborators, creative
                partners, press, and internal brand use.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ─── What's inside ────────────────────────────────────── */}
        <section className="cbk-section" data-testid="cbk-section-inside">
          <div className="cbk-container">
            <Reveal>
              <div className="cbk-eyebrow">01 · Inside the kit</div>
              <h2 className="cbk-h2">What&rsquo;s inside the Canva Brand Kit</h2>
              <p className="cbk-section__lede">
                Everything an approved collaborator needs to keep NYMPHÉ on-brand
                across social, campaigns, decks, and press.
              </p>
            </Reveal>
            <div className="cbk-kit-grid">
              {KIT_ITEMS.map((item, i) => (
                <Reveal key={item} delay={0.06 + i * 0.04}>
                  <div className="cbk-kit-card" data-testid={`cbk-kit-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <span className="cbk-kit-card__dot" aria-hidden="true" />
                    <span className="cbk-kit-card__label">{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Brand direction ──────────────────────────────────── */}
        <section className="cbk-section cbk-direction" data-testid="cbk-section-direction">
          <div className="cbk-direction__glow" aria-hidden="true" />
          <div className="cbk-container">
            <Reveal>
              <div className="cbk-eyebrow">02 · Direction</div>
              <h2 className="cbk-h2">Brand direction</h2>
              <p className="cbk-direction__copy">
                NYMPHÉ is cinematic, private, feminine, seductive, and refined.
                Every creative asset should feel intentional, elegant, discreet,
                and premium.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="cbk-mood-row">
                {MOOD.map((m) => (
                  <span key={m} className="cbk-mood" data-testid={`cbk-mood-${m.toLowerCase()}`}>{m}</span>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── Usage rules ──────────────────────────────────────── */}
        <section className="cbk-section" data-testid="cbk-section-rules">
          <div className="cbk-container">
            <Reveal>
              <div className="cbk-eyebrow">03 · Usage</div>
              <h2 className="cbk-h2">Usage rules</h2>
              <p className="cbk-section__lede">
                These rules protect the NYMPHÉ aesthetic across every creative surface.
              </p>
            </Reveal>

            <ol className="cbk-rules">
              {RULES.map((r, i) => (
                <Reveal key={r} delay={0.06 + i * 0.05}>
                  <li className="cbk-rule" data-testid={`cbk-rule-${i + 1}`}>
                    <span className="cbk-rule__num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="cbk-rule__copy">{r}</span>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        {/* ─── Closing CTA + legal ──────────────────────────────── */}
        <section className="cbk-section cbk-close" data-testid="cbk-section-close">
          <div className="cbk-container cbk-container--narrow">
            <Reveal>
              <h3 className="cbk-close__title">Step inside the Canva Brand Kit.</h3>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="cbk-cta-row cbk-cta-row--centred">
                <a
                  className="cbk-btn cbk-btn--primary"
                  href={CANVA_BRAND_KIT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cbk-footer-canva-cta"
                >
                  Open Canva Brand Kit
                </a>
                <a
                  className="cbk-btn cbk-btn--ghost"
                  href="mailto:hello@nymphe.co?subject=NYMPHÉ%20Canva%20Brand%20Kit%20Access"
                  data-testid="cbk-footer-request-cta"
                >
                  Request Brand Access
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="cbk-legal" data-testid="cbk-legal">
                NYMPHÉ brand assets are provided for approved use only. Use of
                these assets does not imply partnership, endorsement, or
                permission unless granted in writing.
              </p>
            </Reveal>
          </div>
        </section>
      </main>
    </MotionConfig>
  );
};

export default CanvaBrandKitPage;
